import React, { useMemo } from 'react';
import { CarouselItem } from 'ui/components/Carousel/Carousel';
import { CarouselWithDots } from 'ui/components/CarouselWithDots/CarouselWithDots';
import MembershipTierCard from '../MembershipTierCard/MembershipTierCard';
import {
  MembershipTier,
  VARIANT_ATTRIBUTE_NAMES,
} from '@/lib/constants/maverick';
import { getFirstVariant } from 'ui/utils/productHelpers';
import { getFormatters } from 'ui/hooks/formatter';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import { getTierPerks } from '@/lib/utils/maverickHelpers';
import { getPriceValues } from 'ui/utils/variantHelpers';
import {
  AmplitudeActions,
  AmplitudeEvents,
  AmplitudeNavigationTypes,
} from 'whoop-analytics/lib/types';
import { useAnalytics } from '@/hooks/analytics';
import { MembershipTierInfo } from '@/types/maverickTypes';
import { Trans } from '@whoop/i18n/lang/client';
import { UnstyledButton } from 'ui/components/ButtonV2/Button';
import clsx from 'clsx';
import { FeatureAvailabilityDisclaimer } from '@/components/OPLComponents/ChooseMembershipSection/FeatureAvailabilityDisclaimer/FeatureAvailabilityDisclaimer';

interface MembershipTierSelectionProps {
  availableTiers: MembershipTierInfo[];
  setSoftSelectedTier: (tier: MembershipTier) => void;
  isAddingToCart: boolean;
  onCardCTAClick: (tierName: MembershipTier) => Promise<void>;
  softSelectedTier: MembershipTier;
  disclaimerText?: string;
  isUpgrade?: boolean;
  scrollToDisclaimers: () => void;
  showVerticalStack?: boolean;
  showFeatureAvailabilityDisclaimer?: boolean;
}

export function MembershipTierSelection({
  availableTiers,
  setSoftSelectedTier,
  isAddingToCart,
  onCardCTAClick,
  softSelectedTier,
  disclaimerText,
  isUpgrade,
  scrollToDisclaimers,
  showVerticalStack,
  showFeatureAvailabilityDisclaimer,
}: MembershipTierSelectionProps): JSX.Element | null {
  const { currency, language, region, country, isElite, featureAvailability } =
    useSiteStore(
      useShallow((state) => ({
        currency: state.currency,
        language: state.language,
        region: state.region,
        country: state.country,
        isElite: state.membershipStatus.isElite,
        featureAvailability: state.featureAvailability,
      })),
    );
  const { formatDisplayPrice } = getFormatters({ currency, language, region });
  const { trackAmplitudeEvent } = useAnalytics();
  const handleIndexFocused = async (index: number) => {
    setSoftSelectedTier(availableTiers[index].name);
  };

  const startIndex = useMemo(() => {
    return softSelectedTier
      ? availableTiers.findIndex((tier) => tier.name === softSelectedTier)
      : 0;
  }, []);

  const getMembershipTierCards = (showSoftSelected: boolean) => {
    return availableTiers.map((tier) => {
      const familySize = isUpgrade
        ? // in Upgrade flow we filter family variants server side to ensure only the relevant family variant is in membershipProduct
          tier.membershipProduct.variants[0].attributes?.find(
            (attr) => attr.name === VARIANT_ATTRIBUTE_NAMES.FAMILY_SIZE,
          )?.value
        : undefined;

      const { originalPrice, displayPrice, isDiscounted } = getPriceValues(
        getFirstVariant(tier.membershipProduct),
      );
      const formattedDisplayPrice = formatDisplayPrice(displayPrice, {
        roundUp: true,
      });
      const formattedOriginalPrice = isDiscounted
        ? formatDisplayPrice(originalPrice)
        : undefined;
      const rawRenewalPricePerPerson =
        displayPrice && familySize ? displayPrice / familySize : undefined;
      const formattedPricePerPerson = familySize
        ? formatDisplayPrice(rawRenewalPricePerPerson)
        : undefined;

      //Default users who are upgrading to ONE if no tier exists for displaying perks
      const tierToRenderCardPerks = isUpgrade
        ? tier.usersCurrentTier ?? MembershipTier.ONE
        : undefined;

      const perks = getTierPerks({
        tier: tier.name,
        currentTier: tierToRenderCardPerks,
        featureAvailability,
        region,
        country,
        showVerticalStack,
      });

      return {
        key: tier.name,
        component: (
          <MembershipTierCard
            tierName={tier.name}
            perks={perks}
            formattedRenewalPricePerYear={formattedDisplayPrice}
            formattedRenewalPricePerPerson={formattedPricePerPerson}
            usersCurrentTier={tier.usersCurrentTier}
            isAddingToCart={isAddingToCart}
            onCardCTAClick={async () => {
              await onCardCTAClick(tier.name);
              trackAmplitudeEvent(AmplitudeEvents.ViewedJoinFlow, {
                action: AmplitudeActions.Navigation,
                navigation_type: AmplitudeNavigationTypes.MembershipBlock,
                membership_type: tier.name,
              });
            }}
            isSoftSelected={
              showSoftSelected ? tier.name === softSelectedTier : false
            }
            familySize={familySize}
            isUpgrade={isUpgrade}
            isElite={isElite}
            isDiscounted={isDiscounted}
            formattedOriginalPrice={formattedOriginalPrice}
            imageSrc={tier.membershipTierCardImage?.image.url}
          />
        ),
      };
    });
  };

  const additionalMedicalInformationBelow = (
    <p className='pt-4 text-center'>
      <Trans
        i18nKey='membershipTiers:additionalMedicalInformationBelow'
        components={[
          <UnstyledButton
            className='underline underline-offset-2'
            onClick={scrollToDisclaimers}
            data-testid='scroll-to-disclaimers'
          />,
        ]}
      />
    </p>
  );

  return (
    <div className='mx-auto max-w-screen-lg pb-10'>
      <div className='flex justify-center overflow-hidden md:pb-4'>
        <div
          className={clsx(
            'gap-6 p-6',
            showVerticalStack
              ? 'flex flex-col-reverse md:flex-row'
              : 'hidden md:flex',
          )}
        >
          {getMembershipTierCards(true).map((card, index) => (
            <div
              className={clsx(
                'flex h-fit flex-col',
                showFeatureAvailabilityDisclaimer ? 'gap-6' : '',
              )}
              key={card.key}
            >
              <div
                className={clsx(
                  'cursor-pointer',
                  showVerticalStack && softSelectedTier === card.key
                    ? 'my-3.5 md:my-0'
                    : '',
                )}
                onClick={() => handleIndexFocused(index)}
                role='button'
              >
                {card.component}
              </div>
              {disclaimerText &&
              showVerticalStack &&
              card.key === MembershipTier.LIFE ? (
                <FeatureAvailabilityDisclaimer
                  isOpen={softSelectedTier === MembershipTier.LIFE}
                  disclaimerText={disclaimerText}
                  hideForDesktop={true}
                />
              ) : null}
            </div>
          ))}
        </div>
        {!showVerticalStack ? (
          <div className={clsx('max-w-[370px] p-6 md:hidden')}>
            <CarouselWithDots
              showSneakPeek={true}
              setIndexFocused={() => handleIndexFocused}
              startIndex={startIndex}
              contentAboveDots={additionalMedicalInformationBelow}
            >
              {getMembershipTierCards(false).map((card) => (
                <CarouselItem
                  key={card.key}
                  className='flex w-full basis-[fit-content] justify-center'
                >
                  {card.component}
                </CarouselItem>
              ))}
            </CarouselWithDots>
          </div>
        ) : null}
      </div>
      {/* Desktop Additional Medical Information */}
      <div className='flex flex-col gap-4'>
        <div className={clsx('md:block', !showVerticalStack ? 'hidden' : '')}>
          {additionalMedicalInformationBelow}
        </div>
        {disclaimerText ? (
          <FeatureAvailabilityDisclaimer
            isOpen={showFeatureAvailabilityDisclaimer}
            disclaimerText={disclaimerText}
            hideForMobile={showVerticalStack}
          />
        ) : null}
      </div>
    </div>
  );
}
